import React, { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import {
  DashboardStatsService,
  UsageAnalytics,
  AgentPerformance,
} from "@/lib/services/dashboard-stats";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Progress } from "@/components/ui/progress";
import {
  BarChart3,
  LineChart,
  PieChart,
  TrendingUp,
  TrendingDown,
  Activity,
  Clock,
  Target,
  Award,
  Calendar,
  Users,
  Zap,
  Eye,
  Download,
  Share,
  Filter,
} from "lucide-react";

export function EnhancedAnalytics() {
  const { user } = useAuth();
  const [analytics, setAnalytics] = useState<UsageAnalytics | null>(null);
  const [agentPerformance, setAgentPerformance] = useState<AgentPerformance[]>(
    []
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timeRange, setTimeRange] = useState<string>("30d");

  useEffect(() => {
    if (user) {
      loadAnalytics();
    }
  }, [user, timeRange]);

  const loadAnalytics = async () => {
    if (!user) return;

    setLoading(true);
    setError(null);
    try {
      const [analyticsData, performanceData] = await Promise.all([
        DashboardStatsService.getUsageAnalytics(user.id),
        DashboardStatsService.getAgentPerformance(user.id),
      ]);
      setAnalytics(analyticsData);
      setAgentPerformance(performanceData);
    } catch (err) {
      console.error("Error loading analytics:", err);
      setError("Error al cargar los análisis");
    } finally {
      setLoading(false);
    }
  };

  const getGrowthIcon = (value: number) => {
    if (value > 0) return <TrendingUp className="h-4 w-4 text-green-500" />;
    if (value < 0) return <TrendingDown className="h-4 w-4 text-red-500" />;
    return <Activity className="h-4 w-4 text-gray-500" />;
  };

  const getGrowthColor = (value: number) => {
    if (value > 0) return "text-green-600";
    if (value < 0) return "text-red-600";
    return "text-gray-600";
  };

  const formatPercentage = (value: number) => {
    return `${value > 0 ? "+" : ""}${value}%`;
  };

  const getTopAgents = () => {
    return agentPerformance.slice(0, 5);
  };

  const getTotalUsage = () => {
    return agentPerformance.reduce(
      (total, agent) => total + agent.totalUsage,
      0
    );
  };

  const getAverageSuccessRate = () => {
    if (agentPerformance.length === 0) return 0;
    const total = agentPerformance.reduce(
      (sum, agent) => sum + agent.successRate,
      0
    );
    return Math.round(total / agentPerformance.length);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-4 w-24" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-16 mb-2" />
                <Skeleton className="h-3 w-32" />
              </CardContent>
            </Card>
          ))}
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          {[1, 2].map((i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-6 w-32" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-64 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  if (!analytics) {
    return (
      <Alert>
        <AlertDescription>
          No hay datos de análisis disponibles
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      {/* Analytics Controls */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Controles de Análisis
            </span>
            <div className="flex items-center gap-2">
              <Select value={timeRange} onValueChange={setTimeRange}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7d">Últimos 7 días</SelectItem>
                  <SelectItem value="30d">Últimos 30 días</SelectItem>
                  <SelectItem value="90d">Últimos 90 días</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={loadAnalytics} variant="outline" size="icon">
                <Filter className="h-4 w-4" />
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
      </Card>

      {/* Key Metrics */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Activity className="h-4 w-4 text-blue-600" />
              Total de Usos
            </CardTitle>
            <Badge variant="outline">{getTotalUsage()}</Badge>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">
              {getTotalUsage()}
            </div>
            <p className="text-xs text-muted-foreground">
              En el período seleccionado
            </p>
            <Progress
              value={Math.min((getTotalUsage() / 1000) * 100, 100)}
              className="mt-2 h-1"
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Target className="h-4 w-4 text-green-600" />
              Tasa de Éxito
            </CardTitle>
            <Badge variant="outline">{getAverageSuccessRate()}%</Badge>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {getAverageSuccessRate()}%
            </div>
            <p className="text-xs text-muted-foreground">Promedio general</p>
            <Progress value={getAverageSuccessRate()} className="mt-2 h-1" />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Users className="h-4 w-4 text-purple-600" />
              Agentes Activos
            </CardTitle>
            <Badge variant="outline">{agentPerformance.length}</Badge>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-600">
              {agentPerformance.length}
            </div>
            <p className="text-xs text-muted-foreground">En uso</p>
            <Progress
              value={Math.min((agentPerformance.length / 10) * 100, 100)}
              className="mt-2 h-1"
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Zap className="h-4 w-4 text-orange-600" />
              Promedio Diario
            </CardTitle>
            <Badge variant="outline">{Math.round(getTotalUsage() / 30)}</Badge>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">
              {Math.round(getTotalUsage() / 30)}
            </div>
            <p className="text-xs text-muted-foreground">Usos por día</p>
            <Progress
              value={Math.min((getTotalUsage() / 30 / 50) * 100, 100)}
              className="mt-2 h-1"
            />
          </CardContent>
        </Card>
      </div>

      {/* Charts Section */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* Agent Usage Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PieChart className="h-5 w-5 text-blue-600" />
              Distribución de Uso por Agente
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {analytics.agentUsage.slice(0, 8).map((agent, index) => (
                <div key={agent.agent} className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium truncate">
                      {agent.agent}
                    </span>
                    <span className="text-sm text-muted-foreground">
                      {agent.count} ({agent.percentage}%)
                    </span>
                  </div>
                  <Progress value={agent.percentage} className="h-2" />
                </div>
              ))}
              {analytics.agentUsage.length > 8 && (
                <div className="text-center pt-2">
                  <Badge variant="outline">
                    +{analytics.agentUsage.length - 8} más agentes
                  </Badge>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Hourly Usage Pattern */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <LineChart className="h-5 w-5 text-green-600" />
              Patrón de Uso por Hora
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="grid grid-cols-6 gap-2">
                {analytics.hourlyUsage.map((hour) => (
                  <div key={hour.hour} className="text-center">
                    <div className="text-xs text-muted-foreground mb-1">
                      {hour.hour}:00
                    </div>
                    <div
                      className="bg-green-100 rounded"
                      style={{
                        height: `${Math.max(
                          (hour.count /
                            Math.max(
                              ...analytics.hourlyUsage.map((h) => h.count)
                            )) *
                            100,
                          10
                        )}px`,
                      }}>
                      <div className="text-xs text-green-700 text-center pt-1">
                        {hour.count}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="text-center text-sm text-muted-foreground">
                Horas del día (0-23)
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Agent Performance Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Award className="h-5 w-5 text-purple-600" />
              Rendimiento de Agentes
            </span>
            <Badge variant="outline">Top {getTopAgents().length} agentes</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {getTopAgents().map((agent, index) => (
              <div
                key={agent.agentId}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <div className="flex items-center space-x-4">
                  <div className="flex items-center justify-center w-8 h-8 bg-purple-100 rounded-full">
                    <span className="text-sm font-bold text-purple-600">
                      {index + 1}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">
                      {agent.agentName}
                    </p>
                    <p className="text-sm text-gray-600">
                      Último uso:{" "}
                      {agent.lastUsed
                        ? new Date(agent.lastUsed).toLocaleDateString()
                        : "Nunca"}
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-6">
                  <div className="text-center">
                    <p className="text-sm font-medium text-gray-900">
                      {agent.totalUsage}
                    </p>
                    <p className="text-xs text-gray-600">Total</p>
                  </div>

                  <div className="text-center">
                    <p className="text-sm font-medium text-gray-900">
                      {agent.dailyAverage}
                    </p>
                    <p className="text-xs text-gray-600">Promedio/día</p>
                  </div>

                  <div className="text-center">
                    <div className="flex items-center gap-1">
                      {getGrowthIcon(agent.weeklyGrowth)}
                      <span
                        className={`text-sm font-medium ${getGrowthColor(
                          agent.weeklyGrowth
                        )}`}>
                        {formatPercentage(agent.weeklyGrowth)}
                      </span>
                    </div>
                    <p className="text-xs text-gray-600">Crecimiento</p>
                  </div>

                  <div className="text-center">
                    <p className="text-sm font-medium text-gray-900">
                      {agent.successRate}%
                    </p>
                    <p className="text-xs text-gray-600">Éxito</p>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Button size="sm" variant="ghost">
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button size="sm" variant="ghost">
                      <Download className="h-4 w-4" />
                    </Button>
                    <Button size="sm" variant="ghost">
                      <Share className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Usage Trends */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* Daily Usage Trend */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <LineChart className="h-5 w-5 text-blue-600" />
              Tendencia de Uso Diario
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="grid grid-cols-7 gap-1">
                {analytics.dailyUsage.slice(-7).map((day) => (
                  <div key={day.date} className="text-center">
                    <div className="text-xs text-muted-foreground mb-1">
                      {new Date(day.date).toLocaleDateString("es-ES", {
                        day: "2-digit",
                        month: "short",
                      })}
                    </div>
                    <div
                      className="bg-blue-100 rounded"
                      style={{
                        height: `${Math.max(
                          (day.count /
                            Math.max(
                              ...analytics.dailyUsage.map((d) => d.count)
                            )) *
                            100,
                          10
                        )}px`,
                      }}>
                      <div className="text-xs text-blue-700 text-center pt-1">
                        {day.count}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="text-center text-sm text-muted-foreground">
                Últimos 7 días
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Weekly Trend */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-green-600" />
              Tendencia Semanal
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="grid grid-cols-4 gap-2">
                {analytics.weeklyTrend.slice(-4).map((week) => (
                  <div key={week.week} className="text-center">
                    <div className="text-xs text-muted-foreground mb-1">
                      Sem {new Date(week.week).getWeek()}
                    </div>
                    <div
                      className="bg-green-100 rounded"
                      style={{
                        height: `${Math.max(
                          (week.count /
                            Math.max(
                              ...analytics.weeklyTrend.map((w) => w.count)
                            )) *
                            100,
                          10
                        )}px`,
                      }}>
                      <div className="text-xs text-green-700 text-center pt-1">
                        {week.count}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="text-center text-sm text-muted-foreground">
                Últimas 4 semanas
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Insights */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Eye className="h-5 w-5 text-indigo-600" />
            Insights y Recomendaciones
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            <div className="p-4 bg-blue-50 rounded-lg">
              <h4 className="font-medium text-blue-900 mb-2">
                Agente Más Popular
              </h4>
              <p className="text-sm text-blue-700">
                {analytics.agentUsage[0]?.agent || "N/A"} con{" "}
                {analytics.agentUsage[0]?.count || 0} usos
              </p>
            </div>

            <div className="p-4 bg-green-50 rounded-lg">
              <h4 className="font-medium text-green-900 mb-2">Hora Pico</h4>
              <p className="text-sm text-green-700">
                {
                  analytics.hourlyUsage.reduce((max, hour) =>
                    hour.count > max.count ? hour : max
                  ).hour
                }
                :00
              </p>
            </div>

            <div className="p-4 bg-purple-50 rounded-lg">
              <h4 className="font-medium text-purple-900 mb-2">
                Tasa de Éxito
              </h4>
              <p className="text-sm text-purple-700">
                {getAverageSuccessRate()}% - Excelente rendimiento
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Helper function to get week number
declare global {
  interface Date {
    getWeek(): number;
  }
}

Date.prototype.getWeek = function () {
  const d = new Date(this.getTime());
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 4 - (d.getDay() || 7));
  const yearStart = new Date(d.getFullYear(), 0, 1);
  return Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
};
